/**
 * Chatwoot React context for onboarding
 *
 * Exposes `openSupportChat` to layout components; lazy-loads SDK and identifies the user
 * on first open. Renders children only; overlay is a sibling in the app shell.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { auth } from '../../../../firebaseConfig';
import { useOnboardingStore } from '../../store/onboardingStore';
import { resolvePatientDisplayName } from '../../utils/resolvePatientDisplayName';
import { getChatwoot } from './chatwootConfig';
import { ensureOnboardingChatwoot } from './ensureOnboardingChatwoot';
import { identifyOnboardingChatwootUser } from './identifyOnboardingChatwootUser';

export type OnboardingChatwootContextValue = {
  openSupportChat: () => Promise<void>;
  isOpening: boolean;
};

const OnboardingChatwootContext = createContext<OnboardingChatwootContextValue | null>(null);

export type OnboardingChatwootProviderProps = {
  children: ReactNode;
};

/**
 * Provides Chatwoot open state and `openSupportChat` to descendant components.
 *
 * @param {Object} props React children.
 * @return {Object} Context provider element.
 */
export function OnboardingChatwootProvider({ children }: OnboardingChatwootProviderProps) {
  const [isOpening, setIsOpening] = useState(false);
  const isOpeningRef = useRef(false);

  useEffect(() => {
    const onClosed = () => {
      if (isOpeningRef.current) {
        isOpeningRef.current = false;
        setIsOpening(false);
      }
    };
    window.addEventListener('chatwoot:closed', onClosed);
    return () => window.removeEventListener('chatwoot:closed', onClosed);
  }, []);

  const openSupportChat = useCallback(async () => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;
    setIsOpening(true);
    try {
      await ensureOnboardingChatwoot();
      const user = auth.currentUser;
      const { signedUpPatient, verifiedPatientNamesFromAuth } = useOnboardingStore.getState();
      if (user && signedUpPatient) {
        try {
          await identifyOnboardingChatwootUser(
            signedUpPatient,
            resolvePatientDisplayName(signedUpPatient, verifiedPatientNamesFromAuth),
          );
        } catch (err) {
          // Identification only adds support context — it must never keep the chat closed.
          console.warn('[Chatwoot onboarding] Failed to identify user', err);
        }
      }
      getChatwoot()?.toggle('open');
    } catch (err) {
      console.error('[Chatwoot onboarding] Failed to open support chat', err);
    } finally {
      isOpeningRef.current = false;
      setIsOpening(false);
    }
  }, []);

  const value = useMemo(() => ({ openSupportChat, isOpening }), [openSupportChat, isOpening]);

  return <OnboardingChatwootContext.Provider value={value}>{children}</OnboardingChatwootContext.Provider>;
}

/**
 * Returns Chatwoot context; throws when used outside the provider.
 *
 * @return {Object} `openSupportChat` and `isOpening` flag.
 */
export function useOnboardingChatwoot(): OnboardingChatwootContextValue {
  const ctx = useContext(OnboardingChatwootContext);
  if (!ctx) {
    throw new Error('useOnboardingChatwoot must be used within OnboardingChatwootProvider');
  }
  return ctx;
}
